import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Label, Input, Textarea, PrimaryBtn, GhostBtn, Modal } from "@/components/admin/ui";
import ImageUploader from "@/components/admin/ImageUploader";
import { Plus, Pencil, Trash2, Megaphone, Eye, EyeOff } from "lucide-react";

const empty = { title: "", description: "", image_url: "", link: "", active: true };

export default function PromotionsPanel() {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/promotions").then((r) => setPromos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa promozione?")) return;
    await api.delete(`/promotions/${id}`);
    toast.success("Promozione eliminata");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-bold uppercase text-zinc-100">Promozioni</h2>
        <PrimaryBtn onClick={() => setEditing({ ...empty })} data-testid="new-promo-btn">
          <Plus className="h-4 w-4" /> Nuova
        </PrimaryBtn>
      </div>

      {promos.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Nessuna promozione.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((p) => (
            <div key={p.id} data-testid={`admin-promo-${p.id}`} className="border border-zinc-800 bg-zinc-900 overflow-hidden">
              {p.image_url && <img src={p.image_url} alt={p.title} className="h-32 w-full object-cover opacity-80" />}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="h-4 w-4 text-yellow-400" />
                  {p.active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-green-400"><Eye className="h-3 w-3" /> Attiva</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-zinc-500"><EyeOff className="h-3 w-3" /> Nascosta</span>
                  )}
                </div>
                <h3 className="font-heading text-lg font-bold text-zinc-50">{p.title}</h3>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.description || "—"}</p>
                <div className="mt-4 flex gap-2">
                  <GhostBtn onClick={() => setEditing(p)} data-testid={`edit-promo-${p.id}`} className="flex-1">
                    <Pencil className="h-3.5 w-3.5" /> Modifica
                  </GhostBtn>
                  <button onClick={() => remove(p.id)} data-testid={`delete-promo-${p.id}`}
                    className="border border-zinc-700 p-2.5 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors duration-200">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <PromoForm promo={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PromoForm({ promo, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...promo });
  const [saving, setSaving] = useState(false);
  const isEdit = !!promo.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    const payload = { title: form.title, description: form.description, image_url: form.image_url, link: form.link, active: form.active };
    try {
      if (isEdit) await api.put(`/promotions/${promo.id}`, payload);
      else await api.post("/promotions", payload);
      toast.success(isEdit ? "Promozione aggiornata" : "Promozione creata");
      onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? "Modifica promozione" : "Nuova promozione"} onClose={onClose} testid="promo-form">
      <div className="space-y-4">
        <div>
          <Label>Titolo</Label>
          <Input data-testid="promo-title" value={form.title} onChange={set("title")} className="mt-2" />
        </div>
        <div>
          <Label>Descrizione</Label>
          <Textarea data-testid="promo-description" rows={3} value={form.description || ""} onChange={set("description")} className="mt-2" />
        </div>
        <div>
          <Label>Immagine</Label>
          <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} testid="promo-image" />
        </div>
        <div>
          <Label>Link (opzionale)</Label>
          <Input data-testid="promo-link" value={form.link || ""} onChange={set("link")} className="mt-2" placeholder="https://…" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input type="checkbox" data-testid="promo-active" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-yellow-400 h-4 w-4" />
          Promozione attiva (visibile a tutti)
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <GhostBtn onClick={onClose}>Annulla</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving} data-testid="save-promo-btn">{saving ? "Salvataggio…" : "Salva"}</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}
